// api.js
import axios from "axios";

// Variables para controlar el refresh del token
let isRefreshing = false;
let refreshSubscribers = [];

// Función para notificar a los suscriptores cuando el token se renueva
const onRefreshed = () => {
    refreshSubscribers.forEach((callback) => callback());
    refreshSubscribers = [];
};

// Función para agregar suscriptores que esperan el refresh
const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
};

// Instancia base de Axios
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

// Interceptor de respuesta para manejar errores de autenticación
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Si es un error 401 y no hemos intentado refrescar aún
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Si ya se está refrescando, agregar esta petición a la cola
            if (isRefreshing) {
                return new Promise((resolve) => {
                    addRefreshSubscriber(() => {
                        resolve(apiClient(originalRequest));
                    });
                });
            }

            isRefreshing = true;

            try {
                // Intentar refrescar el token
                await apiClient.post("v1/auth/refresh");

                // Token refrescado exitosamente
                isRefreshing = false;
                onRefreshed();

                // Reintentar la petición original
                return apiClient(originalRequest);
            } catch (refreshError) {
                // Si el refresh falla, limpiar y rechazar
                console.error(
                    "❌ [Axios] Error al refrescar token:",
                    refreshError
                );
                isRefreshing = false;
                refreshSubscribers = [];

                // Redirigir al login o manejar el error
                if (typeof window !== "undefined") {
                    // Emitir evento personalizado para que MainRouter maneje el logout
                    window.dispatchEvent(new CustomEvent("auth:token-expired"));
                }

                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Función genérica para manejar todas las solicitudes
const requestHandler = async (config) => {
    try {
        const response = await apiClient.request({
            ...config,
            signal: config.signal,
        });

        return {
            success: true,
            data: response.data,
            requestCode: response.status,
        };
    } catch (error) {
        const err = error;
        const message =
            err?.response?.data?.message || err.message || "Unknown error";
        const status = err?.response?.status || err.status || 500;

        return {
            success: false,
            error: message,
            requestCode: status,
        };
    }
};

// Métodos HTTP
export const http = {
    get: (route, params, config) =>
        requestHandler({
            method: "GET",
            url: route,
            params,
            ...config,
        }),

    post: (route, data, config) =>
        requestHandler({
            method: "POST",
            url: route,
            data,
            ...config,
        }),

    // Corregir la función PUT
    put: (route, data, config) =>
        requestHandler({
            method: "PUT",
            url: route,
            data,
            ...config,
        }),

    patch: (route, data, config) =>
        requestHandler({
            method: "PATCH",
            url: route,
            data,
            ...config,
        }),

    delete: (route, data, config) =>
        requestHandler({
            method: "DELETE",
            url: route,
            data,
            ...config,
        }),
};
